import { prune, type Draft } from './orders-search';

/**
 * The ops dashboard's filters, in the URL for the same reasons the orders
 * table's are (docs/ADMIN-PLAN.md slice 8): a filtered view survives a refresh
 * and can be pasted into a message.
 *
 * The dates are optional and mean something specific by their absence. With no
 * range the dashboard is the live pipeline — today's bookings plus everything
 * still in flight — which is what an operator leaving this screen open all day
 * wants and is not a date range at all. Naming either end turns the screen into
 * a plain historical question: the orders booked in those days, wherever they
 * have got to since. The server says which of the two it answered
 * (`meta.scope`) rather than the UI inferring it.
 *
 * That is a narrower question than the Reports screen's (./insights-search),
 * which buckets by the day a parcel actually landed and charts a series.
 */
export interface DashboardSearch {
    merchant?: number;
    city?: number;
    from?: string;
    to?: string;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function positiveInt(value: unknown): number | undefined {
    const parsed = Number(value);

    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function isoDate(value: unknown): string | undefined {
    return typeof value === 'string' && ISO_DATE.test(value) ? value : undefined;
}

export function validateDashboardSearch(search: Record<string, unknown>): DashboardSearch {
    const { merchant, city, from, to } = search;

    return prune<DashboardSearch>({
        merchant: positiveInt(merchant),
        city: positiveInt(city),
        from: isoDate(from),
        to: isoDate(to),
    });
}

export type { Draft };
